import { css, Theme, useTheme } from '@emotion/react';

import { useRouter } from 'next/router';
import React, { Fragment, useCallback, useMemo } from 'react';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { useWebView } from '@utils/webview';

const mentionCSS = (theme: Theme) => css`
  color: ${theme.colors.main500};
  cursor: pointer;
  font-weight: 500;
  background-color: ${theme.colors.main50};
  padding: 1px 4px;
  border-radius: 4px;
  display: inline;

  &:active {
    background-color: ${theme.colors.main100};
  }
`;

// Mention format: @[type:id:name]
// type: u = user, s = story, c = character
const MENTION_REGEX = /@\[(u|s|c):(\d+):([^\]]+)\]/g;

type MentionType = 'u' | 's' | 'c';

interface ParsedMention {
  type: MentionType;
  id: number;
  name: string;
  fullMatch: string;
  index: number;
}

// URL parsing for link rendering
interface ContentToken {
  kind: 'mention' | 'link';
  start: number;
  end: number;
  mentionType?: MentionType;
  mentionId?: number;
  mentionName?: string;
  url?: string;
  label?: string;
}

const MD_LINK_REGEX = /\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g;
const PLAIN_URL_REGEX = /(https?:\/\/[^\s<]+)/g;

const parseContent = (content: string): ContentToken[] => {
  const tokens: ContentToken[] = [];

  // Parse mentions
  MENTION_REGEX.lastIndex = 0;
  let match;
  while ((match = MENTION_REGEX.exec(content)) !== null) {
    tokens.push({
      kind: 'mention',
      start: match.index,
      end: match.index + match[0].length,
      mentionType: match[1] as MentionType,
      mentionId: parseInt(match[2], 10),
      mentionName: match[3],
    });
  }

  // Parse markdown links [text](url)
  MD_LINK_REGEX.lastIndex = 0;
  while ((match = MD_LINK_REGEX.exec(content)) !== null) {
    const s = match.index;
    const e = s + match[0].length;
    const overlaps = tokens.some((t) => s < t.end && e > t.start);
    if (!overlaps) {
      tokens.push({
        kind: 'link',
        start: s,
        end: e,
        url: match[2],
        label: match[1],
      });
    }
  }

  // Parse plain URLs
  PLAIN_URL_REGEX.lastIndex = 0;
  while ((match = PLAIN_URL_REGEX.exec(content)) !== null) {
    const s = match.index;
    const e = s + match[0].length;
    const overlaps = tokens.some((t) => s < t.end && e > t.start);
    if (!overlaps) {
      tokens.push({
        kind: 'link',
        start: s,
        end: e,
        url: match[0],
        label: match[0],
      });
    }
  }

  tokens.sort((a, b) => a.start - b.start);
  return tokens;
};

interface MentionTextProps {
  content: string;
  className?: string;
}

const MentionText = ({ content, className }: MentionTextProps) => {
  const router = useRouter();
  const theme = useTheme();
  const { platform } = useGlobalContext();
  const { web } = useWebView();

  const navigate = useCallback(
    (path: string) => {
      if (platform === Platform.web) {
        router.push(path);
      } else {
        web.openPage(path);
      }
    },
    [platform, router, web]
  );

  const handleMentionClick = useCallback(
    (e: React.MouseEvent, mention: ParsedMention) => {
      e.stopPropagation();
      e.preventDefault();

      switch (mention.type) {
        case 'u':
          navigate(`/users/${mention.id}`);
          break;
        case 's':
          navigate(`/stories/${mention.id}`);
          break;
        case 'c':
          navigate(`/play/characters/${mention.id}`);
          break;
      }
    },
    [navigate]
  );

  const renderedContent = useMemo(() => {
    const tokens = parseContent(content);

    if (tokens.length === 0) {
      return content;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    tokens.forEach((token, idx) => {
      if (token.start > lastIndex) {
        parts.push(
          <Fragment key={`text-${idx}`}>
            {content.slice(lastIndex, token.start)}
          </Fragment>
        );
      }

      if (token.kind === 'mention') {
        parts.push(
          <span
            key={`mention-${idx}`}
            css={mentionCSS(theme)}
            onClick={(e) =>
              handleMentionClick(e, {
                type: token.mentionType!,
                id: token.mentionId!,
                name: token.mentionName!,
                fullMatch: '',
                index: token.start,
              })
            }
          >
            @{token.mentionName}
          </span>
        );
      } else if (token.kind === 'link') {
        parts.push(
          <a
            key={`link-${idx}`}
            href={token.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {token.label}
          </a>
        );
      }

      lastIndex = token.end;
    });

    if (lastIndex < content.length) {
      parts.push(
        <Fragment key="text-end">{content.slice(lastIndex)}</Fragment>
      );
    }

    return parts;
  }, [content, theme, handleMentionClick]);

  return <span className={className}>{renderedContent}</span>;
};

export default MentionText;

// Utility function to encode a mention
export const encodeMention = (
  type: 'user' | 'story' | 'character',
  id: number,
  name: string
): string => {
  const typeCode = type === 'user' ? 'u' : type === 'story' ? 's' : 'c';
  return `@[${typeCode}:${id}:${name}]`;
};

// Utility function to get display text from encoded content
export const getDisplayText = (content: string): string => {
  return content.replace(MENTION_REGEX, '@$3');
};

// Check if content has any mentions
export const hasMentions = (content: string): boolean => {
  MENTION_REGEX.lastIndex = 0;
  return MENTION_REGEX.test(content);
};
